/**
 * Admin Service - ใช้ UserService ที่มีอยู่แล้ว
 * 
 * Note: โปรเจคมี UserService (ParseV1UserService) ที่จัดการ Admin อยู่แล้ว
 * Service นี้เป็น wrapper ให้ใช้งานง่ายขึ้นสำหรับหน้า Admin
 */
import { getUserService } from '@/domain/user'
import { 
  Admin, 
  AdminGroup, 
  AdminStatus, 
  AdminFormData, 
  AdminQueryParams 
} from './admin.types'

// =========================================
// Type Definitions
// =========================================

interface AdminListResponse {
  admins: Admin[]
  total: number
}

// =========================================
// Helper: Get Session Token
// =========================================

function getSessionToken(): string {
  // Key ที่ระบบใช้
  const SESSION_TOKEN_KEY = 'admin_session_token'
  
  // Try to get from sessionStorage first
  const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY)
  if (sessionToken) {
    return sessionToken
  }
  
  // Fallback to localStorage
  const localToken = localStorage.getItem(SESSION_TOKEN_KEY)
  if (localToken) {
    return localToken
  }
  
  return ''
}

// =========================================
// Mapping: AdminUser -> Admin
// =========================================

function mapToAdmin(user: any): Admin {
  return {
    id: user.id || user.objectId,
    username: user.username || '',
    firstname: user.firstName || user.firstname || '',
    lastname: user.lastName || user.lastname || '',
    admin_tel: user.phone || user.admin_tel || '',
    email: user.email || '',
    line_id: user.lineId || user.line_id || '',
    status: (user.status || 'Active') as AdminStatus,
    admin_group: Number(user.adminGroup ?? user.admin_group ?? AdminGroup.ADMIN),
    permissions: user.permissions || [],
    twoFactorEnabled: user.twoFactorEnabled || false,
    lastLogin: user.lastLogin || user.updatedAt,
    lastLoginIp: user.lastLoginIp || '',
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    createdBy: user.createdBy || '',
  }
}

// =========================================
// Admin Service
// =========================================

export const AdminService = {
  /**
   * ดึงรายการ Admin ทั้งหมด (ใช้ UserService.getAdmins)
   */
  async findAll(params: AdminQueryParams = {}): Promise<AdminListResponse> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] findAll', { hasToken: !!sessionToken, params })
      
      const userService = await getUserService()
      const result = await userService.getAdmins(sessionToken, {
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search || '',
        orderBy: params.orderBy || 'desc',
        orderByField: params.orderByField || 'createdAt',
      })

      console.log('✅ [AdminService] findAll result:', result)

      const admins = (result.items || []).map(mapToAdmin)
      
      return {
        admins,
        total: result.total || 0,
      }
    } catch (error) {
      console.error('❌ [AdminService] findAll error:', error)
      throw error
    }
  },

  /**
   * ดึงข้อมูล Admin ตาม ID (ใช้ UserService.getAdmin)
   */
  async findOne(id: string): Promise<Admin | null> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] findOne id:', id)
      
      const userService = await getUserService()
      const result = await userService.getAdmin(sessionToken, id)
      
      if (!result) return null
      
      return mapToAdmin(result)
    } catch (error) {
      console.error('❌ [AdminService] findOne error:', error)
      throw error
    }
  },

  /**
   * สร้าง Admin ใหม่ (ใช้ UserService.createAdmin)
   */
  async create(data: AdminFormData): Promise<Admin> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] create data:', data)
      
      const userService = await getUserService()
      const result = await userService.createAdmin(sessionToken, {
        username: data.username,
        password: data.password || '',
        firstName: data.firstname,
        lastName: data.lastname,
        phone: data.admin_tel || '',
        adminGroup: data.admin_group,
        permissions: data.permissions || [], // ส่งเป็น Array ตรงๆ
      })

      console.log('✅ [AdminService] create result:', result)

      return mapToAdmin(result)
    } catch (error) {
      console.error('❌ [AdminService] create error:', error)
      throw error
    }
  },

  /**
   * แก้ไขข้อมูล Admin (ใช้ UserService.updateAdmin)
   */
  async update(id: string, data: Partial<AdminFormData>): Promise<Admin> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] update id:', id, 'data:', data)
      
      const userService = await getUserService()
      const result = await userService.updateAdmin(sessionToken, id, {
        firstName: data.firstname,
        lastName: data.lastname,
        phone: data.admin_tel,
        email: data.email,
        password: data.password || undefined,
        adminGroup: data.admin_group,
        status: data.status,
        permissions: data.permissions, // ส่งเป็น Array ตรงๆ
      })

      console.log('✅ [AdminService] update result:', result)

      return mapToAdmin(result)
    } catch (error) {
      console.error('❌ [AdminService] update error:', error)
      throw error
    }
  },

  /**
   * Soft Delete Admin (ใช้ UserService.updateAdmin เพื่อ set status = Cancel)
   */
  async softDelete(id: string): Promise<boolean> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] softDelete id:', id)
      
      const userService = await getUserService()
      await userService.updateAdmin(sessionToken, id, {
        status: 'Cancel',
      })

      console.log('✅ [AdminService] softDelete success')
      return true
    } catch (error) {
      console.error('❌ [AdminService] softDelete error:', error)
      throw error
    }
  },

  /**
   * Hard Delete Admin (ใช้ UserService.deleteAdmin)
   */
  async hardDelete(id: string): Promise<boolean> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] hardDelete id:', id)
      
      const userService = await getUserService()
      await userService.deleteAdmin(sessionToken, id)

      console.log('✅ [AdminService] hardDelete success')
      return true
    } catch (error) {
      console.error('❌ [AdminService] hardDelete error:', error)
      throw error
    }
  },

  /**
   * เปลี่ยนสถานะ Admin
   */
  async changeStatus(id: string, status: AdminStatus): Promise<Admin> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] changeStatus id:', id, 'status:', status)
      
      const userService = await getUserService()
      const result = await userService.updateAdmin(sessionToken, id, {
        status: status,
      })

      console.log('✅ [AdminService] changeStatus result:', result)
      return mapToAdmin(result)
    } catch (error) {
      console.error('❌ [AdminService] changeStatus error:', error)
      throw error
    }
  },

  /**
   * รีเซ็ตรหัสผ่าน Admin
   */
  async resetPassword(id: string, newPassword: string): Promise<boolean> {
    try {
      const sessionToken = getSessionToken()
      console.log('📦 [AdminService] resetPassword id:', id)
      
      const userService = await getUserService()
      await userService.updateAdmin(sessionToken, id, {
        password: newPassword,
      })

      console.log('✅ [AdminService] resetPassword success')
      return true
    } catch (error) {
      console.error('❌ [AdminService] resetPassword error:', error)
      throw error
    }
  },
}

export default AdminService
